import React from 'react';
import styled, { keyframes } from 'styled-components';
// Removed FontAwesome icons - using simple text/HTML elements instead
import { useBooking } from '../../../../contexts/BookingContext';
import {
  isAdultProductCode,
  isChildProductCode,
} from '../membershipUpgradeProductCodes';
import { isChoirFreeAdultTicket } from '../choirFreeAdultTicket';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #2e7d32;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const TicketContainer = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  width: 100%;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(260px, 300px);
  gap: clamp(1rem, 3vw, 1.5rem);
  align-items: start;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const TicketList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ticketCardShadow = `
  0 1px 0 rgba(255, 255, 255, 0.92) inset,
  0 12px 36px rgba(16, 29, 64, 0.07)
`;

const TicketItem = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: clamp(1rem, 2.5vw, 1.25rem);
  border: 1px solid
    ${props =>
      props.$isSelected ? 'rgba(157, 109, 73, 0.45)' : 'rgba(16, 29, 64, 0.08)'};
  box-shadow: ${ticketCardShadow};
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;

  &:hover {
    border-color: rgba(157, 109, 73, 0.35);
    transform: translateY(-2px);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.95) inset,
      0 18px 44px rgba(16, 29, 64, 0.1);
  }
`;

const TicketItemExclusive = styled(TicketItem)`
  border: 1px solid rgba(157, 109, 73, 0.42);
  background: linear-gradient(165deg, #fffef9 0%, #ffffff 55%);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 14px 40px rgba(157, 109, 73, 0.12);

  &:hover {
    border-color: rgba(157, 109, 73, 0.55);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.98) inset,
      0 20px 48px rgba(157, 109, 73, 0.14);
  }
`;

const TicketHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.25rem;
  }
`;

const TicketMainColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const TicketPriceQtyRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-shrink: 0;

  @media (min-width: 768px) {
    flex-direction: column;
    align-items: flex-end;
    justify-content: flex-start;
    gap: 0.65rem;
  }
`;

const TicketName = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.2rem);
  color: ${props => props.theme.colors.primary};
  margin: 0;
  font-weight: 700;
  line-height: 1.2;
`;

const TicketDescription = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: rgba(16, 29, 64, 0.58);
  line-height: 1.5;
  margin: 0;
  font-size: clamp(0.84rem, 1.65vw, 0.92rem);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TicketPrice = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.95rem, 1.9vw, 1.05rem);
  font-weight: 700;
  color: ${props => props.theme.colors.accent};
  white-space: nowrap;
`;

const TicketSectionDivider = styled.div`
  height: 1px;
  background: rgba(16, 29, 64, 0.08);
  margin: clamp(2rem, 5vw, 2.5rem) 0 clamp(1.25rem, 3vw, 1.75rem);
`;

const TicketSectionHeader = styled.div`
  background: linear-gradient(180deg, rgba(241, 239, 234, 0.65) 0%, rgba(255, 255, 255, 0.92) 100%);
  border: 1px solid rgba(16, 29, 64, 0.08);
  border-radius: 14px;
  padding: 1.1rem 1.25rem;
  margin: 0 0 1rem;
`;

const SectionEyebrow = styled.span`
  display: block;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.45);
  margin-bottom: 0.45rem;
`;

const SectionTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.1rem, 2.3vw, 1.3rem);
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.45rem;
  font-weight: 700;
`;

const SectionSubtitle = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.88rem, 1.75vw, 0.95rem);
  color: rgba(16, 29, 64, 0.55);
  margin: 0;
  line-height: 1.55;
`;

const TicketControls = styled.div`
  display: flex;
  align-items: center;
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  background: rgba(16, 29, 64, 0.04);
  border-radius: 999px;
  padding: 0.3rem 0.45rem;
  border: 1px solid rgba(16, 29, 64, 0.1);
`;

const QuantityButton = styled.button`
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 50%;
  background: ${props => props.theme.colors.accent};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
  font-size: 1.2rem;
  line-height: 1;
  flex-shrink: 0;
  touch-action: manipulation;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primary};
    transform: scale(1.08);
  }

  &:disabled {
    background: rgba(16, 29, 64, 0.14);
    cursor: not-allowed;
  }

  @media (max-width: 640px) {
    width: 44px;
    height: 44px;
    font-size: 1.3rem;
  }
`;

const QuantityDisplay = styled.span`
  min-width: 2rem;
  text-align: center;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
`;

const TicketDetails = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.85rem;
  padding-top: 0.85rem;
  border-top: 1px solid rgba(16, 29, 64, 0.07);
`;

const DetailItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.78rem;
  font-weight: 500;
  color: rgba(16, 29, 64, 0.55);
  padding: 0.35rem 0.65rem;
  background: rgba(16, 29, 64, 0.04);
  border-radius: 999px;
  border: 1px solid rgba(16, 29, 64, 0.06);

  svg {
    color: ${props => props.theme.colors.accent};
  }
`;

const InfoText = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: rgba(16, 29, 64, 0.62);
  margin: 0;
  font-size: clamp(0.88rem, 1.75vw, 0.94rem);
  line-height: 1.5;
`;

const UpgradeSavingsBox = styled.div`
  background: linear-gradient(
    165deg,
    rgba(232, 245, 233, 0.65) 0%,
    rgba(255, 255, 255, 0.98) 100%
  );
  border: 1px solid rgba(46, 125, 50, 0.28);
  border-radius: 16px;
  padding: 1.15rem 1.25rem;
  margin-bottom: 1rem;
  position: sticky;
  top: 168px;
  z-index: 10;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 12px 36px rgba(16, 29, 64, 0.06);

  @media (max-width: 992px) {
    position: relative;
    top: 0;
    z-index: 1;
  }
`;

const UpgradeSavingsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.65rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #2e7d32;
  font-weight: 700;
  font-size: 0.8125rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const UpgradeSavingsAmount = styled.div`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.2rem, 2.8vw, 1.45rem);
  font-weight: 700;
  color: #2e7d32;
  margin: 0.45rem 0;
`;

const UpgradeSavingsText = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: rgba(46, 125, 50, 0.88);
  margin: 0.45rem 0;
  font-size: clamp(0.82rem, 1.65vw, 0.88rem);
  line-height: 1.5;
`;

const UpgradeItemsList = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(46, 125, 50, 0.18);
`;

const UpgradeItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0;
  font-size: 0.8rem;
  color: ${props => props.isUsed ? '#999' : '#555'};
  text-decoration: ${props => props.isUsed ? 'line-through' : 'none'};
  opacity: ${props => props.isUsed ? 0.6 : 1};
  transition: all 0.3s ease;
  
  &:not(:last-child) {
    border-bottom: 1px solid rgba(76, 175, 80, 0.2);
  }
`;

const UpgradeItemName = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const UpgradeItemValue = styled.span`
  font-weight: 600;
  color: ${props => props.isUsed ? '#999' : '#2e7d32'};
  margin-left: 0.5rem;
`;

const UsedBadge = styled.span`
  background: #4caf50;
  color: white;
  font-size: 0.7rem;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  font-weight: 600;
  text-transform: uppercase;
`;

const SummaryPanel = styled.div`
  position: sticky;
  top: 168px;
  height: fit-content;
  background: #fff;
  border-radius: 16px;
  padding: clamp(1rem, 2.5vw, 1.25rem);
  border: 1px solid rgba(157, 109, 73, 0.35);
  z-index: 20;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.92) inset,
    0 16px 44px rgba(16, 29, 64, 0.08);

  @media (max-width: 992px) {
    top: 100px;
    z-index: 30;
    margin-top: 0.5rem;
  }

  @media (max-width: 768px) {
    top: 88px;
    position: sticky;
  }
`;

const TotalLabel = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.45);
  margin-bottom: 0.35rem;
`;

const TotalAmount = styled.span`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.65rem, 4vw, 2rem);
  font-weight: 700;
  color: ${props => props.theme.colors.accent};
  line-height: 1.1;
`;

const TodayRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
  margin-top: 0.65rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(16, 29, 64, 0.08);

  strong {
    font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: clamp(0.92rem, 1.8vw, 1rem);
    font-weight: 700;
    color: rgba(16, 29, 64, 0.72);
  }
`;

const Subrow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0.35rem 0;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.86rem, 1.7vw, 0.92rem);
  color: rgba(16, 29, 64, 0.62);
`;

const FamilySaverRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0.2rem 0;
  font-size: 0.9rem;
`;

const FamilySaverLabel = styled.span`
  font-weight: 600;
  color: #2e7d32;
`;

const FamilySaverAmount = styled.span`
  font-weight: 600;
  color: #2e7d32;
`;

const UpgradeCreditRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0.2rem 0;
  font-size: 0.9rem;
  padding: 0.5rem 0;
  border-top: 1px solid rgba(76, 175, 80, 0.3);
  border-bottom: 1px solid rgba(76, 175, 80, 0.3);
`;

const UpgradeCreditLabel = styled.span`
  font-weight: 600;
  color: #2e7d32;
`;

const UpgradeCreditAmount = styled.span`
  font-weight: 600;
  color: #2e7d32;
`;

const ContinueButton = styled.button`
  width: 100%;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: ${props => props.theme.colors.accent};
  color: #fff;
  border: 2px solid ${props => props.theme.colors.accent};
  border-radius: 999px;
  padding: 0.78rem 1.25rem;
  font-size: clamp(0.9rem, 2vw, 0.98rem);
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease,
    box-shadow 0.2s ease;
  margin-top: 0.85rem;
  box-shadow: 0 6px 20px rgba(157, 109, 73, 0.28);

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(16, 29, 64, 0.15);
  }

  &:disabled {
    background: rgba(16, 29, 64, 0.08);
    border-color: rgba(16, 29, 64, 0.1);
    color: rgba(16, 29, 64, 0.38);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const UpgradeStatusMessage = styled.div`
  margin-top: 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${props => props.isSuccess ? '#1b5e20' : '#ef6c00'};
  background: ${props => props.isSuccess ? 'rgba(200, 230, 201, 0.8)' : 'rgba(255, 224, 178, 0.8)'};
  border: 1px solid ${props => props.isSuccess ? '#2e7d32' : '#fb8c00'};
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
`;

const SUMMER_SOLSTICE_PAGE_ID = '0efbadd8-388c-4ad0-ba36-c88282ac634d';
/** Maximum guests per overnight accommodation property (The Vale / Belvoir Lodge) */
const SOLSTICE_OVERNIGHT_CAPACITY = 9;

const SolsticePackageGroupHeader = styled.div`
  background: linear-gradient(135deg, #101d40 0%, #1a2f5e 100%);
  border: 1px solid rgba(157, 109, 73, 0.28);
  border-radius: 14px;
  padding: 1.25rem 1.5rem;
  margin: 0 0 0.85rem;
  box-shadow: 0 8px 28px rgba(16, 29, 64, 0.14);
`;

const SolsticeStandaloneGroupHeader = styled.div`
  background: linear-gradient(180deg, rgba(241, 239, 234, 0.7) 0%, rgba(255, 255, 255, 0.95) 100%);
  border: 1px solid rgba(16, 29, 64, 0.1);
  border-radius: 14px;
  padding: 1.25rem 1.5rem;
  margin: 0 0 0.85rem;
`;

const SolsticeGroupEyebrow = styled.span`
  display: block;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  margin-bottom: 0.35rem;
`;

const SolsticeGroupTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.1rem, 2.3vw, 1.3rem);
  margin: 0 0 0.4rem;
  font-weight: 700;
  line-height: 1.25;
`;

const SolsticeGroupBody = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.84rem, 1.7vw, 0.92rem);
  margin: 0;
  line-height: 1.55;
`;

const SolsticeBadge = styled.span`
  display: inline-block;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.67rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  padding: 0.22rem 0.55rem;
  border-radius: 999px;
  margin-bottom: 0.35rem;
`;

const ANIMAL_PARK_PAGE_IDS = [
  'b0f1a62a-a34a-4c4e-a88d-6fda03a19279',
  '03c1719d-b7ff-43b1-acd9-72c6fdd56f6a',
  '360f48e1-8680-4719-9d2e-fc87a84c974b', // May Day bank holiday castle entry (4 May 2026)
  'b17520e4-20c1-451c-84ea-38c476c5fad7',
];
// Animal Park initial ticket page (Easter booking): hide "Animal Park Free - Promotion" from ticket list here only
const ANIMAL_PARK_INITIAL_PAGE_ID = '833fe805-0f3f-4af2-a552-7e86642746ad';
const isAnimalParkTicket = (name) => name && String(name).toLowerCase().includes('animal park');
const isAnimalParkFreePromotion = (name) => {
  const n = (name || '').toLowerCase();
  return n.includes('animal park free') && n.includes('promotion');
};

const TicketSelection = ({ 
  tickets, 
  selectedTickets, 
  onTicketUpdate, 
  onContinue,
  upgradeCreditAmount,
  sortedUpgradeItems,
  usedUpgradeItems = { child: [], adult: [] },
  originalBookingData,
  upgradeFromBooking,
  isLoadingUpgradeBooking = false,
  upgradeDiscountApplied = false,
  upgradeDiscountCode = null,
  upgradeDiscountMessage = '',
  choirFreeAdultAllowed = false,
  choirLinkMessage = '',
}) => {
  const { getSubtotal, getGroupDiscountAmount, calculateTotal, pageId } = useBooking();
  const isFlowerGardenShow = pageId === '1ced1e1a-b442-4dee-b1e1-ae8b5b7bee09';
  const isCastleGrottoAddLater = pageId === '2f5da61b-0ace-44a0-8d52-51069a6c26df';
  const isAnimalParkPage = ANIMAL_PARK_PAGE_IDS.includes(pageId);
  const isSolsticePage = pageId === SUMMER_SOLSTICE_PAGE_ID;
  const familySaverLabel = isFlowerGardenShow ? 'Flower and Garden Show 2026' : 'Family Saver';
  
  // Detect if this is a membership journey
  const isMembershipJourney = React.useMemo(() => {
    // Check if pageId matches a membership page ID
    if (pageId === '37bd858f-4f04-4e83-a469-05274db0d546' || pageId === '9d7c060b-6088-4bc1-8def-7bddb9b87ef9') {
      return true;
    }
    // Check if all tickets are memberships (webSaleItemTypeID === 4)
    if (tickets && tickets.length > 0) {
      return tickets.every(ticket => ticket.webSaleItemTypeID === 4);
    }
    return false;
  }, [pageId, tickets]);
  // Map selected quantities by id for O(1) lookup
  const qtyById = React.useMemo(() => {
    const map = new Map();
    (selectedTickets || []).forEach(t => {
      if (t && t.webSaleItemId != null) map.set(String(t.webSaleItemId), Number(t.quantity || 0));
    });
    return map;
  }, [selectedTickets]);
  const handleQuantityChange = (ticketId, change) => {
    const ticket = selectedTickets.find(t => t.webSaleItemId === ticketId);
    const currentQuantity = ticket ? ticket.quantity : 0;
    
    let maxAllowed = ticket?.maxPurchaseQty || 50;
    if (isChoirFreeAdultTicket(ticket)) {
      maxAllowed = choirFreeAdultAllowed ? 1 : 0;
    }
    const newQuantity = Math.max(0, Math.min(currentQuantity + change, maxAllowed));
    
    onTicketUpdate(ticketId, newQuantity);
  };

  const getSelectedQuantity = (ticketId) => qtyById.get(String(ticketId)) || 0;

  const getTotalTickets = () => {
    return selectedTickets.reduce((total, ticket) => total + ticket.quantity, 0);
  };
  
  // Calculate upgrade credit based on memberships (including family allowances)
  // Match adult codes to adult memberships, child codes to child memberships, family = 2 adult + 3 child
  const calculateUpgradeCredit = () => {
    if (!upgradeCreditAmount || !sortedUpgradeItems) {
      return 0;
    }
    
    // Helper functions to determine membership type
    const isChildMembership = (ticket) => {
      const name = (ticket.name || '').toLowerCase();
      const description = (ticket.description || '').toLowerCase();
      
      // Exclude free child tickets (under 3, under 2, infant) - these don't count as child memberships
      if (name.includes('under 3') || name.includes('under 2') || name.includes('infant') ||
          description.includes('under 3') || description.includes('under 2') || description.includes('infant')) {
        return false;
      }
      
      return name.includes('child') || 
             name.includes('junior') || 
             description.includes('child') || 
             description.includes('junior');
    };
    const isFamilyMembership = (ticket) => {
      const name = (ticket.name || '').toLowerCase();
      const description = (ticket.description || '').toLowerCase();
      return name.includes('family') || description.includes('family');
    };
    
    // Helper function to check if ticket is a free "under 3" ticket (should not count for any discounts)
    const isFreeUnder3Ticket = (ticket) => {
      const name = (ticket.name || '').toLowerCase();
      const description = (ticket.description || '').toLowerCase();
      return name.includes('under 3') || name.includes('under 2') || name.includes('infant') ||
             description.includes('under 3') || description.includes('under 2') || description.includes('infant');
    };
    
    const isAdultOrConcessionItem = (item) => isAdultProductCode(item.productCode);
    
    // Get available items (handle both array format for backward compatibility and object format)
    const availableChildItems = Array.isArray(sortedUpgradeItems) 
      ? sortedUpgradeItems.filter(item => isChildProductCode(item.productCode))
      : (sortedUpgradeItems.child || []);
    
    const availableAdultItems = Array.isArray(sortedUpgradeItems)
      ? sortedUpgradeItems.filter(item => isAdultOrConcessionItem(item))
      : (sortedUpgradeItems.adult || []);
    
    if (availableChildItems.length === 0 && availableAdultItems.length === 0) {
      return 0;
    }
    
    // Separate memberships by type
    const childMemberships = selectedTickets.filter(ticket => 
      ticket.webSaleItemTypeID === 4 && isChildMembership(ticket) && !isFamilyMembership(ticket)
    );
    const adultMemberships = selectedTickets.filter(ticket => 
      ticket.webSaleItemTypeID === 4 && !isChildMembership(ticket) && !isFamilyMembership(ticket) && !isFreeUnder3Ticket(ticket)
    );
    const familyMemberships = selectedTickets.filter(ticket =>
      ticket.webSaleItemTypeID === 4 && isFamilyMembership(ticket)
    );
    
    const childMembershipCount = childMemberships.reduce((total, ticket) => total + (ticket.quantity || 0), 0);
    const adultMembershipCount = adultMemberships.reduce((total, ticket) => total + (ticket.quantity || 0), 0);
    const familyMembershipCount = familyMemberships.reduce((total, ticket) => total + (ticket.quantity || 0), 0);
    
    if (childMembershipCount === 0 && adultMembershipCount === 0 && familyMembershipCount === 0) {
      return 0;
    }
    
    // Family membership can consume up to 2 adult + 3 child credits per membership
    const totalAdultNeeded = adultMembershipCount + (familyMembershipCount * 2);
    const totalChildNeeded = childMembershipCount + (familyMembershipCount * 3);
    
    // Match child items to child memberships
    const childItemsToUse = availableChildItems.slice(0, totalChildNeeded);
    const childCredit = childItemsToUse.reduce((total, item) => {
      return total + (parseFloat(item.value) || 0);
    }, 0);
    
    // Match adult items to adult memberships
    const adultItemsToUse = availableAdultItems.slice(0, totalAdultNeeded);
    const adultCredit = adultItemsToUse.reduce((total, item) => {
      return total + (parseFloat(item.value) || 0);
    }, 0);
    
    return childCredit + adultCredit;
  };
  
  const appliedUpgradeCredit = calculateUpgradeCredit();
  
  // Helper function to determine if a ticket is a member ticket
  const isMemberTicket = (ticket) => {
    const name = ticket.name?.toLowerCase() || '';
    const description = ticket.description?.toLowerCase() || '';
    
    // Explicitly exclude non-member free tickets
    if (name.includes('under 3') || name.includes('under 2') || name.includes('infant')) {
      return false; // Under 3s are free admission, not members
    }
    if (name.includes('carer') || name.includes('essential carer')) {
      return false; // Carers are free admission, not members
    }
    
    // Check for actual membership indicators
    return (
      name.includes('member') ||           // "Membership", "Member"
      name.includes('hha') ||              // "HHA Member"
      name.includes('friends of') ||       // "Friends of Belvoir"
      name.includes('parkland membership') || // Parkland membership tickets
      description.includes('membership') ||
      description.includes('hha')
    );
  };
  
  // Group tickets into regular and member tickets
  // On Animal Park initial ticket page only: hide "Animal Park Free - Promotion" (from £0.00) from the list
  const regularTickets = tickets
    .filter(t => !isMemberTicket(t))
    .filter(t => choirFreeAdultAllowed || !isChoirFreeAdultTicket(t))
    .filter(t => {
      if (pageId !== ANIMAL_PARK_INITIAL_PAGE_ID) return true;
      return !isAnimalParkFreePromotion(t.name);
    })
    .slice()
    .sort((a, b) => Number(isChoirFreeAdultTicket(b)) - Number(isChoirFreeAdultTicket(a)));
  const memberTickets = tickets.filter(t => isMemberTicket(t));

  // Solstice-specific grouping: packages (overnight, price >= £150) vs standalone experiences
  const solsticePackages = isSolsticePage ? regularTickets.filter(t => t.price >= 150) : [];
  const solsticeStandalones = isSolsticePage ? regularTickets.filter(t => t.price < 150) : [];

  // Live capacity from WordPress — fetched once when the solstice page loads
  const [solsticeCapacity, setSolsticeCapacity] = React.useState(null);
  React.useEffect(() => {
    if (!isSolsticePage) return;
    fetch('https://www.belvoircastle.com/wp/wp-json/custom/v1/solstice-capacity')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.success) setSolsticeCapacity(data); })
      .catch(() => { /* silently ignore — frontend cap still enforces */ });
  }, [isSolsticePage]);

  // Reusable ticket card renderer — accepts an optional badgeType for the solstice page
  const renderTicketCard = (ticket, badgeType = null) => {
    const selectedQuantity = getSelectedQuantity(ticket.webSaleItemId);
    const isSelected = selectedQuantity > 0;

    // Overnight solstice packages: cap at 9 minus however many are already booked across all orders
    let maxAllowed = ticket.maxPurchaseQty || 50;
    if (isChoirFreeAdultTicket(ticket)) {
      maxAllowed = choirFreeAdultAllowed ? 1 : 0;
    }
    let liveRemaining = null;
    let isSoldOut = false;
    if (badgeType === 'package') {
      // Determine which property this ticket maps to (Vale = cheaper, Lodge = more expensive)
      const property = ticket.price < 250 ? 'vale' : 'lodge';
      if (solsticeCapacity) {
        liveRemaining = solsticeCapacity[property]?.remaining ?? SOLSTICE_OVERNIGHT_CAPACITY;
        isSoldOut     = solsticeCapacity[property]?.sold_out ?? false;
        maxAllowed    = Math.min(SOLSTICE_OVERNIGHT_CAPACITY, liveRemaining);
      } else {
        maxAllowed = SOLSTICE_OVERNIGHT_CAPACITY;
      }
    }

    const isExclusiveAnimalPark = isAnimalParkPage && isAnimalParkTicket(ticket.name);
    const displayName = isExclusiveAnimalPark ? 'Exclusive Offer: Free Animal Park Entry' : ticket.name;
    const ItemWrapper = isExclusiveAnimalPark ? TicketItemExclusive : TicketItem;

    return (
      <ItemWrapper key={ticket.webSaleItemId} $isSelected={isSelected}>
        <TicketHeader>
          <TicketMainColumn>
            {badgeType === 'package' && !isSoldOut && (
              <SolsticeBadge style={{
                background: 'rgba(157, 109, 73, 0.1)',
                color: '#9d6d49',
                border: '1px solid rgba(157, 109, 73, 0.28)',
              }}>Complete Package</SolsticeBadge>
            )}
            {badgeType === 'package' && isSoldOut && (
              <SolsticeBadge style={{
                background: 'rgba(211, 47, 47, 0.08)',
                color: '#c62828',
                border: '1px solid rgba(211, 47, 47, 0.25)',
              }}>Sold Out</SolsticeBadge>
            )}
            {badgeType === 'package' && !isSoldOut && liveRemaining !== null && liveRemaining <= 4 && (
              <SolsticeBadge style={{
                background: 'rgba(230, 120, 20, 0.09)',
                color: '#bf6010',
                border: '1px solid rgba(230, 120, 20, 0.22)',
                marginLeft: '0.35rem',
              }}>
                {liveRemaining === 1 ? 'Last place' : `${liveRemaining} places left`}
              </SolsticeBadge>
            )}
            {badgeType === 'standalone' && (
              <SolsticeBadge style={{
                background: 'rgba(16, 29, 64, 0.05)',
                color: 'rgba(16, 29, 64, 0.52)',
                border: '1px solid rgba(16, 29, 64, 0.1)',
              }}>Standalone Experience</SolsticeBadge>
            )}
            <TicketName>{displayName}</TicketName>
            {ticket.description && (
              <TicketDescription>{ticket.description}</TicketDescription>
            )}
          </TicketMainColumn>
          <TicketPriceQtyRow>
            <TicketPrice>
              {ticket.price === 0 ? 'Free' : `from £${ticket.price.toFixed(2)}`}
            </TicketPrice>
            {isSoldOut ? (
              <div style={{
                fontSize: '0.85rem',
                fontWeight: 700,
                color: '#c62828',
                padding: '0.45rem 0.75rem',
                background: 'rgba(211,47,47,0.07)',
                borderRadius: '8px',
                border: '1px solid rgba(211,47,47,0.18)',
              }}>
                Sold Out
              </div>
            ) : (
              <TicketControls>
                <QuantitySelector>
                  <QuantityButton
                    onClick={() => handleQuantityChange(ticket.webSaleItemId, -1)}
                    disabled={selectedQuantity === 0}
                  >
                    −
                  </QuantityButton>
                  <QuantityDisplay>{selectedQuantity}</QuantityDisplay>
                  <QuantityButton
                    onClick={() => handleQuantityChange(ticket.webSaleItemId, 1)}
                    disabled={selectedQuantity >= maxAllowed}
                  >
                    +
                  </QuantityButton>
                </QuantitySelector>
              </TicketControls>
            )}
          </TicketPriceQtyRow>
        </TicketHeader>
        {isSelected && (
          <TicketDetails>
            <DetailItem>
              <span>Qty: {selectedQuantity}</span>
            </DetailItem>
            <DetailItem>
              <span>
                Subtotal:{' '}
                {ticket.price === 0 ? 'Free' : `£${(ticket.price * selectedQuantity).toFixed(2)}`}
              </span>
            </DetailItem>
            {badgeType === 'package' && (
              <DetailItem>
                <span>Max: {SOLSTICE_OVERNIGHT_CAPACITY} guests</span>
              </DetailItem>
            )}
            {!badgeType && ticket.maxPurchaseQty && ticket.maxPurchaseQty !== 999 && (
              <DetailItem>
                <span>Max: {ticket.maxPurchaseQty}</span>
              </DetailItem>
            )}
          </TicketDetails>
        )}
      </ItemWrapper>
    );
  };

  // Debug logging to verify ticket categorization
  React.useEffect(() => {
    if (tickets.length > 0) {

    }
  }, [tickets.length, regularTickets, memberTickets]);

  return (
    <TicketContainer>
      {isCastleGrottoAddLater && (
        <div
          role="note"
          style={{
            marginBottom: '1rem',
            padding: '0.95rem 1.1rem',
            borderRadius: 12,
            background: 'rgba(157, 109, 73, 0.08)',
            border: '1px solid rgba(157, 109, 73, 0.32)',
            color: '#5c3d1e',
            fontSize: '0.95rem',
            lineHeight: 1.55,
          }}
        >
          <strong>Entry ticket required.</strong> This booking is for Santa&apos;s Grotto only.
          Please make sure you already have a Deck the Halls Castle entry ticket for the same visit date.
          Children&apos;s Grotto tickets only — accompanying adults do not need a Grotto ticket.
        </div>
      )}
      {choirLinkMessage && (
        <div
          style={{
            marginBottom: '1rem',
            padding: '0.85rem 1rem',
            borderRadius: 12,
            background: choirFreeAdultAllowed ? 'rgba(46, 125, 50, 0.08)' : 'rgba(198, 40, 40, 0.08)',
            border: choirFreeAdultAllowed ? '1px solid rgba(46, 125, 50, 0.22)' : '1px solid rgba(198, 40, 40, 0.22)',
            color: choirFreeAdultAllowed ? '#1b5e20' : '#b71c1c',
            fontSize: '0.95rem',
            lineHeight: 1.5,
          }}
        >
          {choirLinkMessage}
        </div>
      )}
      <Layout>
        <TicketList>
        {/* For membership journeys, show header and all tickets together */}
        {isMembershipJourney && (
          <TicketSectionHeader>
            <SectionEyebrow>Membership</SectionEyebrow>
            <SectionTitle>Membership Options</SectionTitle>
            <SectionSubtitle>
              Select the membership type(s) you would like to purchase. Your membership will be valid for 12 months from the date of purchase and includes unlimited visits to Belvoir Castle during opening hours.
            </SectionSubtitle>
          </TicketSectionHeader>
        )}
        
        {/* Regular Tickets — with Summer Solstice two-group layout if applicable */}
        {!isMembershipJourney && (
          isSolsticePage ? (
            <>
              {/* ── Complete Weekend Packages ── */}
              {solsticePackages.length > 0 && (
                <>
                  <SolsticePackageGroupHeader>
                    <SolsticeGroupEyebrow style={{ color: 'rgba(157, 109, 73, 0.9)' }}>
                      Complete Weekend Experience
                    </SolsticeGroupEyebrow>
                    <SolsticeGroupTitle style={{ color: '#ffffff' }}>
                      Overnight Packages — Everything Included
                    </SolsticeGroupTitle>
                    <SolsticeGroupBody style={{ color: 'rgba(255, 255, 255, 0.72)' }}>
                      These packages include the Evening Celebration, dinner at the Lodge,
                      overnight accommodation on the estate and the Sunday morning Solstice
                      gathering at the stone circle — the complete weekend experience.
                    </SolsticeGroupBody>
                    <SolsticeGroupBody style={{ color: 'rgba(255, 200, 120, 0.85)', marginTop: '0.55rem', fontSize: '0.8rem' }}>
                      Accommodation is limited to {SOLSTICE_OVERNIGHT_CAPACITY} guests per property. Book early to secure your place.
                    </SolsticeGroupBody>
                  </SolsticePackageGroupHeader>
                  {solsticePackages.map(ticket => renderTicketCard(ticket, 'package'))}
                </>
              )}

              {solsticePackages.length > 0 && solsticeStandalones.length > 0 && (
                <TicketSectionDivider />
              )}

              {/* ── Individual / Standalone Experiences ── */}
              {solsticeStandalones.length > 0 && (
                <>
                  <SolsticeStandaloneGroupHeader>
                    <SolsticeGroupEyebrow style={{ color: 'rgba(16, 29, 64, 0.45)' }}>
                      Individual Experiences
                    </SolsticeGroupEyebrow>
                    <SolsticeGroupTitle style={{ color: '#101d40' }}>
                      Standalone Tickets — Book Just What You Need
                    </SolsticeGroupTitle>
                    <SolsticeGroupBody style={{ color: 'rgba(16, 29, 64, 0.58)' }}>
                      These are standalone tickets. Book the Evening Celebration, the Sunday
                      Stone Circle gathering, or both — each is a separate experience.
                    </SolsticeGroupBody>
                  </SolsticeStandaloneGroupHeader>
                  {solsticeStandalones.map(ticket => renderTicketCard(ticket, 'standalone'))}
                </>
              )}
            </>
          ) : (
            regularTickets.map(ticket => renderTicketCard(ticket))
          )
        )}
        
        {/* Member Tickets Section - Only show for regular journeys */}
        {!isMembershipJourney && memberTickets.length > 0 && (
          <>
            <TicketSectionDivider />
            <TicketSectionHeader>
              <SectionEyebrow>Member pricing</SectionEyebrow>
              <SectionTitle>Member Tickets</SectionTitle>
              <SectionSubtitle>
                Membership card or proof of membership required on entry. Please bring your valid membership card, pass, or confirmation email when you visit.
              </SectionSubtitle>
            </TicketSectionHeader>
            
            {memberTickets.map(ticket => {
              const selectedQuantity = getSelectedQuantity(ticket.webSaleItemId);
              const isSelected = selectedQuantity > 0;
              const maxAllowed = ticket.maxPurchaseQty || 50;
              const isExclusiveAnimalPark = isAnimalParkPage && isAnimalParkTicket(ticket.name);
              const displayName = isExclusiveAnimalPark ? 'Exclusive Offer: Free Animal Park Entry' : ticket.name;
              const ItemWrapper = isExclusiveAnimalPark ? TicketItemExclusive : TicketItem;
              
              return (
                <ItemWrapper key={ticket.webSaleItemId} $isSelected={isSelected}>
              <TicketHeader>
                <TicketMainColumn>
                  <TicketName>{displayName}</TicketName>
                  {ticket.description && (
                    <TicketDescription>{ticket.description}</TicketDescription>
                  )}
                </TicketMainColumn>
                <TicketPriceQtyRow>
                  <TicketPrice>from £{ticket.price.toFixed(2)}</TicketPrice>
                  <TicketControls>
                  <QuantitySelector>
                    <QuantityButton
                      onClick={() => handleQuantityChange(ticket.webSaleItemId, -1)}
                      disabled={selectedQuantity === 0}
                    >
                      −
                    </QuantityButton>
                    <QuantityDisplay>{selectedQuantity}</QuantityDisplay>
                    <QuantityButton
                      onClick={() => handleQuantityChange(ticket.webSaleItemId, 1)}
                      disabled={selectedQuantity >= maxAllowed}
                    >
                      +
                    </QuantityButton>
                  </QuantitySelector>
                </TicketControls>
                </TicketPriceQtyRow>
              </TicketHeader>

              {isSelected && (
                <TicketDetails>
                  <DetailItem>
                    <span>Qty: {selectedQuantity}</span>
                  </DetailItem>
                  <DetailItem>
                    <span>Subtotal: £{(ticket.price * selectedQuantity).toFixed(2)}</span>
                  </DetailItem>
                  {ticket.maxPurchaseQty && ticket.maxPurchaseQty !== 999 && (
                    <DetailItem>
                      <span>Max: {ticket.maxPurchaseQty}</span>
                    </DetailItem>
                  )}
                </TicketDetails>
              )}
            </ItemWrapper>
          );
        })}
          </>
        )}
        
        {/* For membership journeys, show all tickets (they're all memberships) - after the header */}
        {isMembershipJourney && tickets.map(ticket => {
          const selectedQuantity = getSelectedQuantity(ticket.webSaleItemId);
          const isSelected = selectedQuantity > 0;
          const maxAllowed = ticket.maxPurchaseQty || 50;
          const isExclusiveAnimalPark = isAnimalParkPage && isAnimalParkTicket(ticket.name);
          const displayName = isExclusiveAnimalPark ? 'Exclusive Offer: Free Animal Park Entry' : ticket.name;
          const ItemWrapper = isExclusiveAnimalPark ? TicketItemExclusive : TicketItem;
          
          return (
            <ItemWrapper key={ticket.webSaleItemId} $isSelected={isSelected}>
              <TicketHeader>
                <TicketMainColumn>
                  <TicketName>{displayName}</TicketName>
                  {ticket.description && (
                    <TicketDescription>{ticket.description}</TicketDescription>
                  )}
                </TicketMainColumn>
                <TicketPriceQtyRow>
                  <TicketPrice>from £{ticket.price.toFixed(2)}</TicketPrice>
                  <TicketControls>
                  <QuantitySelector>
                    <QuantityButton
                      onClick={() => handleQuantityChange(ticket.webSaleItemId, -1)}
                      disabled={selectedQuantity === 0}
                    >
                      −
                    </QuantityButton>
                    <QuantityDisplay>{selectedQuantity}</QuantityDisplay>
                    <QuantityButton
                      onClick={() => handleQuantityChange(ticket.webSaleItemId, 1)}
                      disabled={selectedQuantity >= maxAllowed}
                    >
                      +
                    </QuantityButton>
                  </QuantitySelector>
                </TicketControls>
                </TicketPriceQtyRow>
              </TicketHeader>

              {isSelected && (
                <TicketDetails>
                  <DetailItem>
                    <span>Qty: {selectedQuantity}</span>
                  </DetailItem>
                  <DetailItem>
                    <span>Subtotal: £{(ticket.price * selectedQuantity).toFixed(2)}</span>
                  </DetailItem>
                  {ticket.maxPurchaseQty && ticket.maxPurchaseQty !== 999 && (
                    <DetailItem>
                      <span>Max: {ticket.maxPurchaseQty}</span>
                    </DetailItem>
                  )}
                </TicketDetails>
              )}
            </ItemWrapper>
          );
        })}
        </TicketList>

        {/* Upgrade Savings Display */}
        {(upgradeCreditAmount > 0 || isLoadingUpgradeBooking || upgradeFromBooking) && (
          <UpgradeSavingsBox>
            <UpgradeSavingsHeader>
              🎉 Upgrade Credit
            </UpgradeSavingsHeader>
            {isLoadingUpgradeBooking ? (
              <>
                <UpgradeSavingsAmount>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Spinner />
                    Loading upgrade credit...
                  </span>
                </UpgradeSavingsAmount>
                <UpgradeSavingsText>
                  Fetching your original booking details...
                  {upgradeFromBooking && (
                    <span style={{ display: 'block', marginTop: '0.25rem', fontSize: '0.8rem' }}>
                      Booking: {upgradeFromBooking}
                    </span>
                  )}
                </UpgradeSavingsText>
              </>
            ) : upgradeCreditAmount > 0 ? (
              <>
                <UpgradeSavingsAmount>
                  £{upgradeCreditAmount.toFixed(2)} Available
                  {appliedUpgradeCredit > 0 && (
                    <span style={{ fontSize: '0.9rem', fontWeight: 400, marginLeft: '0.5rem', display: 'block', marginTop: '0.25rem' }}>
                      £{appliedUpgradeCredit.toFixed(2)} applied ({getTotalTickets()} membership{getTotalTickets() !== 1 ? 's' : ''})
                    </span>
                  )}
                </UpgradeSavingsAmount>
                <UpgradeSavingsText>
                  Your original booking value will be credited towards this membership.
                  {upgradeFromBooking && (
                    <span style={{ display: 'block', marginTop: '0.25rem', fontSize: '0.8rem' }}>
                      Booking: {upgradeFromBooking}
                    </span>
                  )}
                </UpgradeSavingsText>
            {upgradeDiscountMessage && (
              <UpgradeStatusMessage isSuccess={upgradeDiscountApplied}>
                {upgradeDiscountMessage}
              </UpgradeStatusMessage>
            )}
              </>
            ) : upgradeFromBooking ? (
              <>
                <UpgradeSavingsAmount>
                  <span style={{ color: '#999', fontSize: '0.9rem' }}>
                    No upgrade credit available
                  </span>
                </UpgradeSavingsAmount>
                <UpgradeSavingsText>
                  Unable to load upgrade credit for this booking.
                  {upgradeFromBooking && (
                    <span style={{ display: 'block', marginTop: '0.25rem', fontSize: '0.8rem' }}>
                      Booking: {upgradeFromBooking}
                    </span>
                  )}
                </UpgradeSavingsText>
              </>
            ) : null}
            {!isLoadingUpgradeBooking && sortedUpgradeItems && (() => {
              // Helper functions to determine membership types
              const isChildMembership = (ticket) => {
                const name = (ticket.name || '').toLowerCase();
                const description = (ticket.description || '').toLowerCase();
                
                // Exclude free child tickets (under 3, under 2, infant) - these don't count as child memberships
                if (name.includes('under 3') || name.includes('under 2') || name.includes('infant') ||
                    description.includes('under 3') || description.includes('under 2') || description.includes('infant')) {
                  return false;
                }
                
                return name.includes('child') || 
                       name.includes('junior') || 
                       description.includes('child') || 
                       description.includes('junior');
              };
              const isFamilyMembershipLocal = (ticket) => {
                const name = (ticket.name || '').toLowerCase();
                const description = (ticket.description || '').toLowerCase();
                return name.includes('family') || description.includes('family');
              };
              
              const isAdultOrConcessionItem = (item) => isAdultProductCode(item.productCode);
              
              // Get available items (handle both array format for backward compatibility and object format)
              const availableChildItems = Array.isArray(sortedUpgradeItems) 
                ? sortedUpgradeItems.filter(item => isChildProductCode(item.productCode, item.productDescription))
                : (sortedUpgradeItems.child || []);
              
              const availableAdultItems = Array.isArray(sortedUpgradeItems)
                ? sortedUpgradeItems.filter(item => isAdultOrConcessionItem(item))
                : (sortedUpgradeItems.adult || []);
              
              // Items that have already been redeemed (tracked) - display only
              const usedChildItems = Array.isArray(usedUpgradeItems)
                ? []
                : (usedUpgradeItems.child || []);
              const usedAdultItems = Array.isArray(usedUpgradeItems)
                ? []
                : (usedUpgradeItems.adult || []);
              
              // Separate memberships by type
              const isFamilyMembership = (ticket) => {
                const name = (ticket.name || '').toLowerCase();
                const description = (ticket.description || '').toLowerCase();
                return name.includes('family') || description.includes('family');
              };
              
              // Helper function to check if ticket is a free "under 3" ticket (should not count for any discounts)
              const isFreeUnder3Ticket = (ticket) => {
                const name = (ticket.name || '').toLowerCase();
                const description = (ticket.description || '').toLowerCase();
                return name.includes('under 3') || name.includes('under 2') || name.includes('infant') ||
                       description.includes('under 3') || description.includes('under 2') || description.includes('infant');
              };
              
              const childMemberships = selectedTickets.filter(ticket => 
                ticket.webSaleItemTypeID === 4 && isChildMembership(ticket) && !isFamilyMembership(ticket)
              );
              const adultMemberships = selectedTickets.filter(ticket => 
                ticket.webSaleItemTypeID === 4 && !isChildMembership(ticket) && !isFamilyMembershipLocal(ticket) && !isFreeUnder3Ticket(ticket)
              );
              const familyMemberships = selectedTickets.filter(ticket =>
                ticket.webSaleItemTypeID === 4 && isFamilyMembershipLocal(ticket)
              );
              
              const childMembershipCount = childMemberships.reduce((total, ticket) => total + (ticket.quantity || 0), 0);
              const adultMembershipCount = adultMemberships.reduce((total, ticket) => total + (ticket.quantity || 0), 0);
              const familyMembershipCount = familyMemberships.reduce((total, ticket) => total + (ticket.quantity || 0), 0);
              
              // Family membership can consume up to 2 adult + 3 child credits per membership
              const totalAdultNeeded = adultMembershipCount + (familyMembershipCount * 2);
              const totalChildNeeded = childMembershipCount + (familyMembershipCount * 3);
              
              const totalItems = availableChildItems.length + availableAdultItems.length;
              const totalUsed = Math.min(totalChildNeeded, availableChildItems.length) + Math.min(totalAdultNeeded, availableAdultItems.length);
              
              if (totalItems === 0) return null;
              
              return (
                <UpgradeItemsList>
                  <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.4rem', fontWeight: 600 }}>
                    Available Credits ({totalUsed} of {totalItems} used):
                  </div>
                  
                  {/* Child Items Section */}
                  {availableChildItems.length > 0 && (
                    <>
                      <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '0.5rem', marginBottom: '0.3rem', fontWeight: 600, textTransform: 'uppercase' }}>
                        Child Items (for Child Memberships only)
                      </div>
                      {availableChildItems.map((item, index) => {
                        const isUsed = index < totalChildNeeded;
                        return (
                          <UpgradeItem key={`child-${index}`} isUsed={isUsed}>
                            <UpgradeItemName>
                              {isUsed && <UsedBadge>✓ Used</UsedBadge>}
                              {item.productDescription || item.productCode}
                              <span style={{ fontSize: '0.7rem', color: '#888', marginLeft: '0.5rem' }}>(Child)</span>
                            </UpgradeItemName>
                            <UpgradeItemValue isUsed={isUsed}>
                              £{parseFloat(item.value).toFixed(2)}
                            </UpgradeItemValue>
                          </UpgradeItem>
                        );
                      })}
                    </>
                  )}
                  
                  {/* Adult Items Section */}
                  {availableAdultItems.length > 0 && (
                    <>
                      <div style={{ fontSize: '0.7rem', color: '#888', marginTop: availableChildItems.length > 0 ? '0.5rem' : '0', marginBottom: '0.3rem', fontWeight: 600, textTransform: 'uppercase' }}>
                        Adult Items (for Adult Memberships only)
                      </div>
                      {availableAdultItems.map((item, index) => {
                        const isUsed = index < totalAdultNeeded;
                        return (
                          <UpgradeItem key={`adult-${index}`} isUsed={isUsed}>
                            <UpgradeItemName>
                              {isUsed && <UsedBadge>✓ Used</UsedBadge>}
                              {item.productDescription || item.productCode}
                              <span style={{ fontSize: '0.7rem', color: '#888', marginLeft: '0.5rem' }}>(Adult)</span>
                            </UpgradeItemName>
                            <UpgradeItemValue isUsed={isUsed}>
                              £{parseFloat(item.value).toFixed(2)}
                            </UpgradeItemValue>
                          </UpgradeItem>
                        );
                      })}
                    </>
                  )}
                  
                  {/* Previously used items (not available for credit) */}
                  {(usedChildItems.length > 0 || usedAdultItems.length > 0) && (
                    <>
                      <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.75rem', marginBottom: '0.3rem', fontWeight: 600 }}>
                        Already used credits (not available)
                      </div>
                      
                      {usedChildItems.length > 0 && (
                        <>
                          <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '0.2rem', marginBottom: '0.2rem', fontWeight: 600, textTransform: 'uppercase' }}>
                            Child Items
                          </div>
                          {usedChildItems.map((item, index) => (
                            <UpgradeItem key={`used-child-${index}`} isUsed>
                              <UpgradeItemName>
                                <UsedBadge>✓ Used</UsedBadge>
                                {item.productDescription || item.productCode}
                                <span style={{ fontSize: '0.7rem', color: '#888', marginLeft: '0.5rem' }}>(Child)</span>
                              </UpgradeItemName>
                              <UpgradeItemValue isUsed>
                                £{parseFloat(item.value).toFixed(2)}
                              </UpgradeItemValue>
                            </UpgradeItem>
                          ))}
                        </>
                      )}
                      
                      {usedAdultItems.length > 0 && (
                        <>
                          <div style={{ fontSize: '0.7rem', color: '#888', marginTop: usedChildItems.length > 0 ? '0.4rem' : '0.2rem', marginBottom: '0.2rem', fontWeight: 600, textTransform: 'uppercase' }}>
                            Adult Items
                          </div>
                          {usedAdultItems.map((item, index) => (
                            <UpgradeItem key={`used-adult-${index}`} isUsed>
                              <UpgradeItemName>
                                <UsedBadge>✓ Used</UsedBadge>
                                {item.productDescription || item.productCode}
                                <span style={{ fontSize: '0.7rem', color: '#888', marginLeft: '0.5rem' }}>(Adult)</span>
                              </UpgradeItemName>
                              <UpgradeItemValue isUsed>
                                £{parseFloat(item.value).toFixed(2)}
                              </UpgradeItemValue>
                            </UpgradeItem>
                          ))}
                        </>
                      )}
                    </>
                  )}
                  
                  {totalUsed === 0 && (
                    <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.5rem', fontStyle: 'italic', textAlign: 'center' }}>
                      Add memberships to use these credits
                    </div>
                  )}
                </UpgradeItemsList>
              );
            })()}
          </UpgradeSavingsBox>
        )}

        <SummaryPanel>
          {getTotalTickets() > 0 ? (
            <>
              <TotalLabel>
                Total ({getTotalTickets()} {isMembershipJourney ? 'membership' : 'ticket'}{getTotalTickets() !== 1 ? 's' : ''})
              </TotalLabel>
              <Subrow>
                <span>Subtotal</span>
                <span>£{getSubtotal().toFixed(2)}</span>
              </Subrow>
              {getGroupDiscountAmount() > 0 && !isMembershipJourney && (
                <FamilySaverRow>
                  <FamilySaverLabel>{familySaverLabel}</FamilySaverLabel>
                  <FamilySaverAmount>-£{getGroupDiscountAmount().toFixed(2)}</FamilySaverAmount>
                </FamilySaverRow>
              )}
              {appliedUpgradeCredit > 0 && (
                <UpgradeCreditRow>
                  <UpgradeCreditLabel>Upgrade Credit</UpgradeCreditLabel>
                  <UpgradeCreditAmount>-£{appliedUpgradeCredit.toFixed(2)}</UpgradeCreditAmount>
                </UpgradeCreditRow>
              )}
              <TodayRow>
                <strong>Today</strong>
                <TotalAmount>
                  £{Math.max(0, calculateTotal() - appliedUpgradeCredit).toFixed(2)}
                </TotalAmount>
              </TodayRow>
              {onContinue && (
                <ContinueButton onClick={onContinue}>
                  Continue →
                </ContinueButton>
              )}
            </>
          ) : (
            <InfoText>
              {isMembershipJourney
                ? "Select the membership(s) you'd like to purchase. You can adjust quantities using the + and - buttons."
                : "Select the tickets you'd like to purchase. You can adjust quantities using the + and - buttons."
              }
            </InfoText>
          )}
        </SummaryPanel>
      </Layout>
    </TicketContainer>
  );
};

export default TicketSelection;